/**
 * Mobile Theme Customizer — Full-Viewport Overlays
 * Touch-friendly full-screen UI for site and profile theme customization.
 * Replaces the desktop modal with a mobile-optimized experience.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { X, Save } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { getThemeByKey } from '@theme/tokens';
import type { PresetThemeKey } from '@theme/tokens';
import { useTheme } from '../../context/ThemeContext';
import { useAuth } from '../../context/AuthContext';
import { normalizeCustomization } from '../../utils/colorNormalization';
import { cacheSiteCustomization } from '../../utils/themeCache';
import { calculateNavbarBg, getCardBackground } from '../../utils/backgroundHelpers';
import { getThemeAnimations } from '@theme/animation-config';
import { profileEventBus } from '../../lib/profileEventBus';

import { fetchWithAuth } from '../../lib/fetchWithAuth';
const PRESET_OPTIONS: { key: PresetThemeKey; label: string }[] = [
  { key: 'ocean-turtle', label: 'Ocean' },
  { key: 'mountain-eagle', label: 'Mountain' },
  { key: 'sun-fire', label: 'Sun Fire' },
  { key: 'lightning-bolt', label: 'Lightning' },
  { key: 'air-clouds', label: 'Air' },
];

// ---------------------------------------------------------------------------
// Full Viewport Overlay Shell
// ---------------------------------------------------------------------------

function FullViewportOverlay({
  isOpen,
  onClose,
  title,
  children,
}: {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
}) {
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = ''; };
    }
  }, [isOpen]);

  return createPortal(
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 30 }}
          transition={{ type: 'spring', damping: 28, stiffness: 300 }}
          className="fixed inset-0 z-[101] flex flex-col"
          style={{ backgroundColor: 'var(--card-bg)', background: 'var(--card-bg)' }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div
            className="flex-shrink-0 flex items-center justify-between px-5"
            style={{
              borderBottom: '1px solid rgba(255,255,255,0.08)',
              paddingTop: 'max(env(safe-area-inset-top, 0px), 12px)',
              paddingBottom: 12,
            }}
          >
            <h2 className="text-lg font-bold" style={{ color: 'var(--text-color, #fff)' }}>
              {title}
            </h2>
            <button
              onClick={onClose}
              className="w-9 h-9 rounded-full flex items-center justify-center"
              style={{ backgroundColor: 'rgba(255,255,255,0.08)' }}
            >
              <X size={20} style={{ color: 'var(--text-color, #fff)' }} />
            </button>
          </div>

          {/* Scrollable content */}
          <div
            className="flex-1 overflow-y-auto px-5 pb-8 pt-4"
            style={{ WebkitOverflowScrolling: 'touch' }}
          >
            {children}
          </div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function SectionHeader({ label }: { label: string }) {
  return (
    <h3
      className="text-xs font-semibold uppercase tracking-wider mt-5 mb-3"
      style={{ color: 'var(--text-color)', opacity: 0.4 }}
    >
      {label}
    </h3>
  );
}

function ColorRow({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
}) {
  const hexValue = value.startsWith('#') ? value : '#14b8a6';

  return (
    <div className="mb-4">
      <label
        className="block text-sm font-medium mb-2"
        style={{ color: 'var(--text-color, #fff)', opacity: 0.8 }}
      >
        {label}
      </label>
      <div className="flex gap-3 items-center">
        <input
          type="color"
          value={hexValue}
          onChange={(e) => onChange(e.target.value)}
          className="w-12 h-12 rounded-xl border-2 cursor-pointer"
          style={{ borderColor: 'rgba(255,255,255,0.1)', backgroundColor: 'transparent' }}
        />
        <input
          type="text"
          value={hexValue}
          onChange={(e) => {
            const v = e.target.value;
            if (/^#[0-9a-fA-F]{0,6}$/.test(v)) onChange(v);
          }}
          className="flex-1 px-4 py-3 rounded-lg text-sm font-mono"
          style={{
            backgroundColor: 'rgba(255,255,255,0.06)',
            color: 'var(--text-color, #fff)',
            border: '1px solid rgba(255,255,255,0.08)',
          }}
        />
      </div>
    </div>
  );
}

function OpacitySlider({
  label,
  value,
  onChange,
  min = 0,
  max = 100,
  suffix = '%',
}: {
  label: string;
  value: number;
  onChange: (v: number) => void;
  min?: number;
  max?: number;
  suffix?: string;
}) {
  return (
    <div className="mb-4">
      <div className="flex justify-between mb-1.5">
        <span className="text-sm font-medium" style={{ color: 'var(--text-color)', opacity: 0.8 }}>
          {label}
        </span>
        <span className="text-sm font-mono" style={{ color: 'var(--text-color)', opacity: 0.5 }}>
          {value}{suffix}
        </span>
      </div>
      <input
        type="range"
        min={min}
        max={max}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="w-full h-2 rounded-full appearance-none cursor-pointer"
        style={{ backgroundColor: 'rgba(255,255,255,0.1)', accentColor: 'var(--color-accent, #14b8a6)' }}
      />
    </div>
  );
}

function ToggleRow({
  label,
  value,
  onChange,
}: {
  label: string;
  value: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <button
      onClick={() => onChange(!value)}
      className="flex items-center justify-between w-full py-3 px-4 rounded-xl mb-3"
      style={{ backgroundColor: 'rgba(255,255,255,0.04)' }}
    >
      <span className="text-sm font-medium" style={{ color: 'var(--text-color, #fff)' }}>
        {label}
      </span>
      <div
        className="w-11 h-6 rounded-full relative transition-colors"
        style={{ backgroundColor: value ? 'var(--color-accent, #14b8a6)' : 'rgba(255,255,255,0.15)' }}
      >
        <div
          className="w-5 h-5 rounded-full absolute top-0.5 transition-all"
          style={{ backgroundColor: '#fff', left: value ? 22 : 2 }}
        />
      </div>
    </button>
  );
}

function OptionSelector<T extends string>({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: T;
  options: { value: T; label: string }[];
  onChange: (v: T) => void;
}) {
  return (
    <div className="mb-4">
      <label
        className="block text-sm font-medium mb-2"
        style={{ color: 'var(--text-color, #fff)', opacity: 0.8 }}
      >
        {label}
      </label>
      <div className="flex gap-2">
        {options.map((opt) => (
          <button
            key={opt.value}
            onClick={() => onChange(opt.value)}
            className="flex-1 py-2.5 rounded-xl text-xs font-semibold transition-all"
            style={{
              backgroundColor: value === opt.value
                ? 'var(--color-accent, #14b8a6)'
                : 'rgba(255,255,255,0.06)',
              color: value === opt.value ? '#fff' : 'var(--text-color, #fff)',
              opacity: value === opt.value ? 1 : 0.7,
            }}
          >
            {opt.label}
          </button>
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Mobile Site Theme Customizer
// ---------------------------------------------------------------------------

export function MobileSiteThemeCustomizer({
  isOpen,
  onClose,
}: {
  isOpen: boolean;
  onClose: () => void;
}) {
  const { user } = useAuth();
  const { theme, currentPreset } = useTheme();
  const userId = user?.id;

  // Core colors
  const [primaryColor, setPrimaryColor] = useState(theme.primaryColor || '#006064');
  const [accentColor, setAccentColor] = useState(theme.accentColor || '#14b8a6');
  const [textColor, setTextColor] = useState(theme.textColor || '#ffffff');
  const [highlightColor, setHighlightColor] = useState(theme.highlightColor || '#14b8a6');

  // Surface colors
  const [cardBg, setCardBg] = useState('#1a1a2e');
  const [navbarBg, setNavbarBg] = useState('#0a0a1a');
  const [tabBarBg, setTabBarBg] = useState('#1e3a5f');
  const [dropdownColor, setDropdownColor] = useState('#1a1a2e');

  // Opacity
  const [navbarOpacity, setNavbarOpacity] = useState(92);
  const [tabBarOpacity, setTabBarOpacity] = useState(88);
  const [cardOpacity, setCardOpacity] = useState(100);

  // Background
  const [gradientEnabled, setGradientEnabled] = useState(true);
  const [gradientAngle, setGradientAngle] = useState(135);

  const [activePreset, setActivePreset] = useState<PresetThemeKey | null>(currentPreset as PresetThemeKey || 'ocean-turtle');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isOpen || !userId) return;
    (async () => {
      try {
        const res = await fetchWithAuth(`/api/users/${userId}/site-theme`, { credentials: 'include' });
        if (res.ok) {
          const data = await res.json();
          if (data.customization) {
            const c = data.customization;
            setPrimaryColor(c.primaryColor || '#006064');
            setAccentColor(c.accentColor || '#14b8a6');
            setTextColor(c.textColor || '#ffffff');
            setHighlightColor(c.highlightColor || '#14b8a6');
            setCardBg(c.cardBg || '#1a1a2e');
            setNavbarBg(c.navbarBg || '#0a0a1a');
            setTabBarBg(c.tabBarBg || '#1e3a5f');
            setDropdownColor(c.dropdownColor || '#1a1a2e');
            setNavbarOpacity(c.navbarOpacity ?? 92);
            setTabBarOpacity(c.tabBarOpacity ?? 88);
            setCardOpacity(c.cardOpacity ?? 100);
            setGradientEnabled(c.gradientEnabled ?? true);
            setGradientAngle(c.gradientAngle ?? 135);
          }
          if (data.baseTheme) setActivePreset(data.baseTheme as PresetThemeKey);
        }
      } catch { /* use defaults */ }
    })();
  }, [isOpen, userId]);

  const applyPreset = useCallback((key: PresetThemeKey) => {
    const preset = getThemeByKey(key);
    if (!preset) return;
    setActivePreset(key);
    setPrimaryColor(preset.primaryColor || preset.accentColor);
    setAccentColor(preset.accentColor);
    setTextColor(preset.textColor);
    setHighlightColor(preset.accentColor);
    setGradientEnabled(true);
    if (preset.cardBg) setCardBg(preset.cardBg);
    if (preset.navbarBg) setNavbarBg(preset.navbarBg);
    if (preset.tabBarBg) setTabBarBg(preset.tabBarBg);
    if (preset.dropdownColor) setDropdownColor(preset.dropdownColor);
    setNavbarOpacity(preset.navbarOpacity ?? 92);
    setTabBarOpacity(preset.tabBarOpacity ?? 88);
    setCardOpacity(100);
  }, []);

  const handleSave = async () => {
    if (!userId) return;
    setSaving(true);
    try {
      const basePreset = getThemeByKey(activePreset || 'ocean-turtle');
      const animations = getThemeAnimations(activePreset || 'ocean-turtle');
      const selectedCharacter = animations.characters?.[0]?.id || 'turtle';
      const selectedDecoration = animations.decorations?.[0]?.id || 'coral';

      const customization = {
        ...basePreset,
        primaryColor,
        accentColor,
        textColor,
        highlightColor,
        cardBg,
        navbarBg,
        tabBarBg,
        dropdownColor,
        gradientEnabled,
        gradientAngle,
        gradientFavorability: 50,
        gradientStrength: 100,
        navbarOpacity,
        tabBarOpacity,
        cardOpacity,
        // Required fields not present in ThemeTokens
        cardGradientEnabled: basePreset?.cardGradientEnabled ?? false,
        cardGradientAngle: 135,
        cardGradientColor1: basePreset?.cardGradientColor1 || accentColor,
        cardGradientColor2: basePreset?.cardGradientColor2 || primaryColor,
        bubbleColor: accentColor,
        turtleColor: basePreset?.cardGradientColor2 || primaryColor || accentColor,
        coralColors: [accentColor, basePreset?.secondaryColor || accentColor, basePreset?.cardGradientColor2 || accentColor],
        profileFrameColor: accentColor,
        profileFrameShape: 'circle' as const,
        profileNameAlign: 'center' as const,
        profileFrameDesign: 'coral' as const,
        animationsEnabled: true,
        selectedCharacter,
        selectedDecoration,
      };

      const normalized = normalizeCustomization(customization);

      const res = await fetchWithAuth(`/api/users/${userId}/site-theme`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          baseTheme: activePreset || 'ocean-turtle',
          customization: normalized,
          animations,
          animationsEnabled: true,
        }),
      });

      if (res.ok) {
        const computedNavbarBg = calculateNavbarBg(navbarBg || primaryColor, navbarOpacity);
        const computedTabBarBg = calculateNavbarBg(tabBarBg || primaryColor, tabBarOpacity);
        const computedCardBg = getCardBackground(
          { primaryColor, secondaryColor: normalized.secondaryColor || primaryColor, cardBg: normalized.cardBg || primaryColor, cardGradientColor1: normalized.cardGradientColor1, cardGradientColor2: normalized.cardGradientColor2 },
          { cardGradientEnabled: normalized.cardGradientEnabled ?? false, cardGradientAngle: normalized.cardGradientAngle ?? 135, cardGradientFavorability: normalized.cardGradientFavorability ?? 50, cardGradientStrength: normalized.cardGradientStrength ?? 100, cardOpacity },
          primaryColor,
        );
        const computedAppBg = normalized.gradientEnabled
          ? `linear-gradient(${normalized.gradientAngle || 135}deg, ${primaryColor} 0%, ${normalized.secondaryColor || primaryColor} ${(normalized.gradientFavorability ?? 50) * 2}%)`
          : primaryColor;

        cacheSiteCustomization({
          baseColors: normalized,
          computedColors: {
            textColor, accentColor, cardBg: computedCardBg, navbarBg: computedNavbarBg, tabBarBg: computedTabBarBg,
            primaryColor, secondaryColor: normalized.secondaryColor || primaryColor,
            appBg: computedAppBg,
            cardGradientColor1: normalized.cardGradientColor1,
            cardGradientColor2: normalized.cardGradientColor2,
          },
          settings: { ...normalized, animationsEnabled: true },
          animations,
        });

        // Directly set CSS vars on root for instant visual update (no waiting for React re-render)
        const root = document.documentElement;
        root.style.setProperty('--app-bg', computedAppBg);
        root.style.setProperty('--text-color', textColor);
        root.style.setProperty('--color-accent', accentColor);
        root.style.setProperty('--card-bg', computedCardBg);
        root.style.setProperty('--navbar-bg', computedNavbarBg);
        root.style.setProperty('--tab-bar-bg', computedTabBarBg);
        root.style.setProperty('--primary-color', primaryColor);

        window.dispatchEvent(new CustomEvent('siteThemeUpdated', {
          detail: { customization: normalized, animations, animationsEnabled: true, baseTheme: activePreset }
        }));

        onClose();
      }
    } catch { /* silent */ }
    finally { setSaving(false); }
  };

  return (
    <FullViewportOverlay isOpen={isOpen} onClose={onClose} title="Site Theme">
      {/* Presets */}
      <div className="mb-4">
        <label className="block text-sm font-medium mb-3" style={{ color: 'var(--text-color, #fff)', opacity: 0.8 }}>
          Presets
        </label>
        <div className="grid grid-cols-3 gap-2">
          {PRESET_OPTIONS.map(({ key, label }) => {
            const isActive = activePreset === key;
            return (
              <button
                key={key}
                onClick={() => applyPreset(key)}
                className="py-3 rounded-xl text-sm font-medium transition-all"
                style={{
                  backgroundColor: isActive ? 'var(--color-accent, #14b8a6)' : 'rgba(255,255,255,0.06)',
                  color: isActive ? '#fff' : 'var(--text-color, #fff)',
                  border: isActive ? '2px solid var(--color-accent, #14b8a6)' : '2px solid transparent',
                  opacity: isActive ? 1 : 0.7,
                }}
              >
                {label}
              </button>
            );
          })}
        </div>
      </div>

      <SectionHeader label="Core Colors" />
      <ColorRow label="Primary Color" value={primaryColor} onChange={setPrimaryColor} />
      <ColorRow label="Accent Color" value={accentColor} onChange={setAccentColor} />
      <ColorRow label="Text Color" value={textColor} onChange={setTextColor} />
      <ColorRow label="Highlight Color" value={highlightColor} onChange={setHighlightColor} />

      <SectionHeader label="Surfaces" />
      <ColorRow label="Card Background" value={cardBg} onChange={setCardBg} />
      <ColorRow label="Navbar Background" value={navbarBg} onChange={setNavbarBg} />
      <ColorRow label="Tab Bar Background" value={tabBarBg} onChange={setTabBarBg} />
      <ColorRow label="Dropdown Color" value={dropdownColor} onChange={setDropdownColor} />

      <SectionHeader label="Opacity" />
      <OpacitySlider label="Navbar Opacity" value={navbarOpacity} onChange={setNavbarOpacity} />
      <OpacitySlider label="Tab Bar Opacity" value={tabBarOpacity} onChange={setTabBarOpacity} />
      <OpacitySlider label="Card Opacity" value={cardOpacity} onChange={setCardOpacity} />

      <SectionHeader label="Background" />
      <ToggleRow label="Gradient Background" value={gradientEnabled} onChange={setGradientEnabled} />
      <OpacitySlider label="Gradient Angle" value={gradientAngle} onChange={setGradientAngle} min={0} max={360} suffix="°" />

      <button
        onClick={handleSave}
        disabled={saving}
        className="w-full py-4 rounded-xl font-semibold text-base flex items-center justify-center gap-2 mt-4"
        style={{ backgroundColor: 'var(--color-accent, #14b8a6)', color: '#fff', opacity: saving ? 0.6 : 1 }}
      >
        <Save size={18} />
        {saving ? 'Saving...' : 'Save & Apply'}
      </button>

      <div className="h-8" />
    </FullViewportOverlay>
  );
}

// ---------------------------------------------------------------------------
// Mobile Profile Theme Customizer (with tabs)
// ---------------------------------------------------------------------------

export function MobileProfileThemeCustomizer({
  isOpen,
  onClose,
  userId,
}: {
  isOpen: boolean;
  onClose: () => void;
  userId: string;
}) {
  const { theme, currentPreset } = useTheme();
  const [activeTab, setActiveTab] = useState<'theme' | 'design'>('theme');

  // Theme tab
  const [primaryColor, setPrimaryColor] = useState(theme.primaryColor || '#006064');
  const [accentColor, setAccentColor] = useState(theme.accentColor || '#14b8a6');
  const [textColor, setTextColor] = useState(theme.textColor || '#ffffff');
  const [highlightColor, setHighlightColor] = useState(theme.highlightColor || '#14b8a6');
  const [cardBg, setCardBg] = useState('#1a1a2e');
  const [navbarBg, setNavbarBg] = useState('#0a0a1a');
  const [navbarOpacity, setNavbarOpacity] = useState(92);
  const [cardOpacity, setCardOpacity] = useState(100);
  const [gradientEnabled, setGradientEnabled] = useState(true);
  const [activePreset, setActivePreset] = useState<PresetThemeKey | null>(currentPreset as PresetThemeKey || 'ocean-turtle');

  // Card Design tab
  const [profileFrameColor, setProfileFrameColor] = useState('#14b8a6');
  const [profileFrameShape, setProfileFrameShape] = useState<'circle' | 'square'>('circle');
  const [profileFrameDesign, setProfileFrameDesign] = useState<'coral' | 'waves' | 'bubbles'>('coral');
  const [profileNameAlign, setProfileNameAlign] = useState<'left' | 'center' | 'right'>('center');
  const [profilePicturePosition, setProfilePicturePosition] = useState<'left' | 'right'>('left');
  const [profilePictureSize, setProfilePictureSize] = useState<'small' | 'medium' | 'large'>('medium');
  const [cardGradientEnabled, setCardGradientEnabled] = useState(false);
  const [cardGradientAngle, setCardGradientAngle] = useState(135);
  const [cardGradientColor1, setCardGradientColor1] = useState('#006064');
  const [cardGradientColor2, setCardGradientColor2] = useState('#14b8a6');

  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isOpen || !userId) return;
    (async () => {
      try {
        const res = await fetchWithAuth(`/api/profile/${userId}`, { credentials: 'include' });
        if (res.ok) {
          const rawData = await res.json();
          const data = rawData.user || rawData;
          const c = data.profileCustomization;
          if (c) {
            setPrimaryColor(c.primaryColor || '#006064');
            setAccentColor(c.accentColor || '#14b8a6');
            setTextColor(c.textColor || '#ffffff');
            setHighlightColor(c.highlightColor || '#14b8a6');
            setCardBg(c.cardBg || '#1a1a2e');
            setNavbarBg(c.navbarBg || '#0a0a1a');
            setNavbarOpacity(c.navbarOpacity ?? 92);
            setCardOpacity(c.cardOpacity ?? 100);
            setGradientEnabled(c.gradientEnabled ?? true);
            setProfileFrameColor(c.profileFrameColor || '#14b8a6');
            setProfileFrameShape(c.profileFrameShape || 'circle');
            setProfileNameAlign(c.profileNameAlign || 'center');
            setProfilePicturePosition(c.profilePicturePosition || 'left');
            setProfilePictureSize(c.profilePictureSize || 'medium');
            setCardGradientEnabled(c.cardGradientEnabled ?? false);
            setCardGradientAngle(c.cardGradientAngle ?? 135);
            setCardGradientColor1(c.cardGradientColor1 || '#006064');
            setCardGradientColor2(c.cardGradientColor2 || '#14b8a6');
            setProfileFrameDesign(c.profileFrameDesign || 'coral');
            if (c.baseTheme) setActivePreset(c.baseTheme as PresetThemeKey);
          }
        }
      } catch { /* use defaults */ }
    })();
  }, [isOpen, userId]);

  const applyPreset = useCallback((key: PresetThemeKey) => {
    const preset = getThemeByKey(key);
    if (!preset) return;
    setActivePreset(key);
    setPrimaryColor(preset.primaryColor || preset.accentColor);
    setAccentColor(preset.accentColor);
    setTextColor(preset.textColor);
    setHighlightColor(preset.accentColor);
    setGradientEnabled(true);
    if (preset.cardBg) setCardBg(preset.cardBg);
    if (preset.navbarBg) setNavbarBg(preset.navbarBg);
    setNavbarOpacity(preset.navbarOpacity ?? 92);
    setCardOpacity(100);
    setCardGradientEnabled(preset.cardGradientEnabled ?? false);
    if (preset.cardGradientColor1) setCardGradientColor1(preset.cardGradientColor1);
    if (preset.cardGradientColor2) setCardGradientColor2(preset.cardGradientColor2);
    setCardGradientAngle(preset.cardGradientAngle ?? 135);
  }, []);

  const handleSave = async () => {
    if (!userId) return;
    setSaving(true);
    try {
      const basePreset = getThemeByKey(activePreset || 'ocean-turtle');
      const animations = getThemeAnimations(activePreset || 'ocean-turtle');
      const selectedCharacter = animations.characters?.[0]?.id || 'turtle';
      const selectedDecoration = animations.decorations?.[0]?.id || 'coral';

      const customization = {
        ...basePreset,
        baseTheme: activePreset,
        primaryColor, accentColor, textColor, highlightColor,
        cardBg, navbarBg, navbarOpacity, cardOpacity,
        gradientEnabled,
        gradientAngle: 135, gradientFavorability: 50, gradientStrength: 100, tabBarOpacity: 88,
        profileFrameColor, profileFrameShape, profileNameAlign,
        profilePicturePosition, profilePictureSize,
        cardGradientEnabled,
        cardGradientAngle,
        cardGradientFavorability: 50,
        cardGradientStrength: 100,
        cardGradientColor1: cardGradientEnabled ? cardGradientColor1 : (basePreset?.cardGradientColor1 || accentColor),
        cardGradientColor2: cardGradientEnabled ? cardGradientColor2 : (basePreset?.cardGradientColor2 || primaryColor),
        // Required fields not present in ThemeTokens
        bubbleColor: accentColor,
        turtleColor: basePreset?.cardGradientColor2 || primaryColor || accentColor,
        coralColors: [accentColor, basePreset?.secondaryColor || accentColor, basePreset?.cardGradientColor2 || accentColor],
        profileFrameDesign,
        animationsEnabled: true,
        selectedCharacter,
        selectedDecoration,
      };

      const normalized = normalizeCustomization(customization);

      // Profile customization saves to /api/profile/:userId (same as desktop)
      const res = await fetchWithAuth(`/api/profile/${userId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          customization: {
            ...normalized,
            animations,
            animationsEnabled: true,
          },
        }),
      });

      if (res.ok) {
        profileEventBus.emitProfileUpdated(userId);
        window.dispatchEvent(new CustomEvent('profileThemeUpdated', {
          detail: { customization: normalized, animations, animationsEnabled: true, baseTheme: activePreset }
        }));
        onClose();
      }
    } catch { /* silent */ }
    finally { setSaving(false); }
  };

  return (
    <FullViewportOverlay isOpen={isOpen} onClose={onClose} title="Profile Theme">
      {/* Tabs */}
      <div className="flex gap-2 mb-5">
        <button
          onClick={() => setActiveTab('theme')}
          className="flex-1 py-2.5 rounded-xl text-sm font-semibold transition-all"
          style={{
            backgroundColor: activeTab === 'theme' ? 'var(--color-accent)' : 'rgba(255,255,255,0.06)',
            color: activeTab === 'theme' ? '#fff' : 'var(--text-color, #fff)',
            opacity: activeTab === 'theme' ? 1 : 0.7,
          }}
        >
          Theme
        </button>
        <button
          onClick={() => setActiveTab('design')}
          className="flex-1 py-2.5 rounded-xl text-sm font-semibold transition-all"
          style={{
            backgroundColor: activeTab === 'design' ? 'var(--color-accent)' : 'rgba(255,255,255,0.06)',
            color: activeTab === 'design' ? '#fff' : 'var(--text-color, #fff)',
            opacity: activeTab === 'design' ? 1 : 0.7,
          }}
        >
          Card Design
        </button>
      </div>

      {activeTab === 'theme' && (
        <>
          <div className="mb-4">
            <label className="block text-sm font-medium mb-3" style={{ color: 'var(--text-color, #fff)', opacity: 0.8 }}>
              Presets
            </label>
            <div className="grid grid-cols-3 gap-2">
              {PRESET_OPTIONS.map(({ key, label }) => {
                const isActive = activePreset === key;
                return (
                  <button
                    key={key}
                    onClick={() => applyPreset(key)}
                    className="py-3 rounded-xl text-sm font-medium transition-all"
                    style={{
                      backgroundColor: isActive ? 'var(--color-accent, #14b8a6)' : 'rgba(255,255,255,0.06)',
                      color: isActive ? '#fff' : 'var(--text-color, #fff)',
                      border: isActive ? '2px solid var(--color-accent, #14b8a6)' : '2px solid transparent',
                      opacity: isActive ? 1 : 0.7,
                    }}
                  >
                    {label}
                  </button>
                );
              })}
            </div>
          </div>

          <SectionHeader label="Core Colors" />
          <ColorRow label="Primary Color" value={primaryColor} onChange={setPrimaryColor} />
          <ColorRow label="Accent Color" value={accentColor} onChange={setAccentColor} />
          <ColorRow label="Text Color" value={textColor} onChange={setTextColor} />
          <ColorRow label="Highlight Color" value={highlightColor} onChange={setHighlightColor} />

          <SectionHeader label="Surfaces" />
          <ColorRow label="Card Background" value={cardBg} onChange={setCardBg} />
          <ColorRow label="Navbar Background" value={navbarBg} onChange={setNavbarBg} />

          <SectionHeader label="Opacity" />
          <OpacitySlider label="Navbar Opacity" value={navbarOpacity} onChange={setNavbarOpacity} />
          <OpacitySlider label="Card Opacity" value={cardOpacity} onChange={setCardOpacity} />

          <SectionHeader label="Background" />
          <ToggleRow label="Gradient Background" value={gradientEnabled} onChange={setGradientEnabled} />
        </>
      )}

      {activeTab === 'design' && (
        <>
          <SectionHeader label="Profile Frame" />
          <ColorRow label="Frame Color" value={profileFrameColor} onChange={setProfileFrameColor} />
          <OptionSelector
            label="Frame Shape"
            value={profileFrameShape}
            options={[{ value: 'circle', label: 'Circle' }, { value: 'square', label: 'Square' }]}
            onChange={setProfileFrameShape}
          />
          <OptionSelector
            label="Frame Animation"
            value={profileFrameDesign}
            options={[{ value: 'coral', label: 'Coral' }, { value: 'waves', label: 'Waves' }, { value: 'bubbles', label: 'Bubbles' }]}
            onChange={setProfileFrameDesign}
          />

          <SectionHeader label="Profile Picture" />
          <OptionSelector
            label="Position"
            value={profilePicturePosition}
            options={[{ value: 'left', label: 'Left' }, { value: 'right', label: 'Right' }]}
            onChange={setProfilePicturePosition}
          />
          <OptionSelector
            label="Size"
            value={profilePictureSize}
            options={[{ value: 'small', label: 'Small' }, { value: 'medium', label: 'Medium' }, { value: 'large', label: 'Large' }]}
            onChange={setProfilePictureSize}
          />

          <SectionHeader label="Name Alignment" />
          <OptionSelector
            label="Alignment"
            value={profileNameAlign}
            options={[{ value: 'left', label: 'Left' }, { value: 'center', label: 'Center' }, { value: 'right', label: 'Right' }]}
            onChange={setProfileNameAlign}
          />

          <SectionHeader label="Card Gradient" />
          <ToggleRow label="Card Gradient" value={cardGradientEnabled} onChange={setCardGradientEnabled} />
          {cardGradientEnabled && (
            <>
              <OpacitySlider label="Gradient Angle" value={cardGradientAngle} onChange={setCardGradientAngle} min={0} max={360} suffix="°" />
              <ColorRow label="Gradient Color 1" value={cardGradientColor1} onChange={setCardGradientColor1} />
              <ColorRow label="Gradient Color 2" value={cardGradientColor2} onChange={setCardGradientColor2} />
            </>
          )}
        </>
      )}

      <button
        onClick={handleSave}
        disabled={saving}
        className="w-full py-4 rounded-xl font-semibold text-base flex items-center justify-center gap-2 mt-4"
        style={{ backgroundColor: 'var(--color-accent, #14b8a6)', color: '#fff', opacity: saving ? 0.6 : 1 }}
      >
        <Save size={18} />
        {saving ? 'Saving...' : 'Save & Apply'}
      </button>

      <div className="h-8" />
    </FullViewportOverlay>
  );
}

// ---------------------------------------------------------------------------
// Mobile Community Theme Customizer
// ---------------------------------------------------------------------------

export function MobileCommunityThemeCustomizer({
  isOpen,
  onClose,
  communityId,
}: {
  isOpen: boolean;
  onClose: () => void;
  communityId: string;
}) {
  const { theme, currentPreset } = useTheme();
  const [activeTab, setActiveTab] = useState<'theme' | 'design'>('theme');

  // Theme tab
  const [primaryColor, setPrimaryColor] = useState(theme.primaryColor || '#006064');
  const [accentColor, setAccentColor] = useState(theme.accentColor || '#14b8a6');
  const [textColor, setTextColor] = useState(theme.textColor || '#ffffff');
  const [highlightColor, setHighlightColor] = useState(theme.highlightColor || '#14b8a6');
  const [cardBg, setCardBg] = useState('#1a1a2e');
  const [navbarBg, setNavbarBg] = useState('#0a0a1a');
  const [navbarOpacity, setNavbarOpacity] = useState(92);
  const [cardOpacity, setCardOpacity] = useState(100);
  const [gradientEnabled, setGradientEnabled] = useState(true);
  const [activePreset, setActivePreset] = useState<PresetThemeKey | null>(currentPreset as PresetThemeKey || 'ocean-turtle');

  // Card Design tab
  const [cardGradientEnabled, setCardGradientEnabled] = useState(false);
  const [cardGradientAngle, setCardGradientAngle] = useState(135);
  const [cardGradientColor1, setCardGradientColor1] = useState('#006064');
  const [cardGradientColor2, setCardGradientColor2] = useState('#14b8a6');

  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isOpen || !communityId) return;
    (async () => {
      try {
        const res = await fetchWithAuth(`/api/communities/${communityId}`, { credentials: 'include' });
        if (res.ok) {
          const data = await res.json();
          const comm = data.community || data;
          const c = comm.communityCustomization;
          if (c) {
            setPrimaryColor(c.primaryColor || '#006064');
            setAccentColor(c.accentColor || '#14b8a6');
            setTextColor(c.textColor || '#ffffff');
            setHighlightColor(c.highlightColor || '#14b8a6');
            setCardBg(c.cardBg || '#1a1a2e');
            setNavbarBg(c.navbarBg || '#0a0a1a');
            setNavbarOpacity(c.navbarOpacity ?? 92);
            setCardOpacity(c.cardOpacity ?? 100);
            setGradientEnabled(c.gradientEnabled ?? true);
            setCardGradientEnabled(c.cardGradientEnabled ?? false);
            setCardGradientAngle(c.cardGradientAngle ?? 135);
            setCardGradientColor1(c.cardGradientColor1 || '#006064');
            setCardGradientColor2(c.cardGradientColor2 || '#14b8a6');
            if (c.baseTheme) setActivePreset(c.baseTheme as PresetThemeKey);
          }
        }
      } catch { /* use defaults */ }
    })();
  }, [isOpen, communityId]);

  const applyPreset = useCallback((key: PresetThemeKey) => {
    const preset = getThemeByKey(key);
    if (!preset) return;
    setActivePreset(key);
    setPrimaryColor(preset.primaryColor || preset.accentColor);
    setAccentColor(preset.accentColor);
    setTextColor(preset.textColor);
    setHighlightColor(preset.accentColor);
    setGradientEnabled(true);
    if (preset.cardBg) setCardBg(preset.cardBg);
    if (preset.navbarBg) setNavbarBg(preset.navbarBg);
    setNavbarOpacity(preset.navbarOpacity ?? 92);
    setCardOpacity(100);
    setCardGradientEnabled(preset.cardGradientEnabled ?? false);
    if (preset.cardGradientColor1) setCardGradientColor1(preset.cardGradientColor1);
    if (preset.cardGradientColor2) setCardGradientColor2(preset.cardGradientColor2);
    setCardGradientAngle(preset.cardGradientAngle ?? 135);
  }, []);

  const handleSave = async () => {
    if (!communityId) return;
    setSaving(true);
    try {
      const basePreset = getThemeByKey(activePreset || 'ocean-turtle');
      const animations = getThemeAnimations(activePreset || 'ocean-turtle');
      const selectedCharacter = animations.characters?.[0]?.id || 'turtle';
      const selectedDecoration = animations.decorations?.[0]?.id || 'coral';

      const customization = {
        ...basePreset,
        baseTheme: activePreset,
        primaryColor, accentColor, textColor, highlightColor,
        cardBg, navbarBg, navbarOpacity, cardOpacity,
        gradientEnabled,
        gradientAngle: 135, gradientFavorability: 50, gradientStrength: 100, tabBarOpacity: 88,
        cardGradientEnabled,
        cardGradientAngle,
        cardGradientFavorability: 50,
        cardGradientStrength: 100,
        cardGradientColor1: cardGradientEnabled ? cardGradientColor1 : (basePreset?.cardGradientColor1 || accentColor),
        cardGradientColor2: cardGradientEnabled ? cardGradientColor2 : (basePreset?.cardGradientColor2 || primaryColor),
        bubbleColor: accentColor,
        turtleColor: basePreset?.cardGradientColor2 || primaryColor || accentColor,
        coralColors: [accentColor, basePreset?.secondaryColor || accentColor, basePreset?.cardGradientColor2 || accentColor],
        animationsEnabled: true,
        selectedCharacter,
        selectedDecoration,
      };

      const normalized = normalizeCustomization(customization);

      const res = await fetchWithAuth(`/api/communities/${communityId}/theme`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          customization: {
            ...normalized,
            animations,
            animationsEnabled: true,
          },
        }),
      });

      if (res.ok) {
        window.dispatchEvent(new CustomEvent('communityThemeUpdated', {
          detail: { communityId, customization: normalized, animations, animationsEnabled: true, baseTheme: activePreset }
        }));
        onClose();
      }
    } catch { /* silent */ }
    finally { setSaving(false); }
  };

  return (
    <FullViewportOverlay isOpen={isOpen} onClose={onClose} title="Community Theme">
      {/* Tabs */}
      <div className="flex gap-2 mb-5">
        <button
          onClick={() => setActiveTab('theme')}
          className="flex-1 py-2.5 rounded-xl text-sm font-semibold transition-all"
          style={{
            backgroundColor: activeTab === 'theme' ? 'var(--color-accent)' : 'rgba(255,255,255,0.06)',
            color: activeTab === 'theme' ? '#fff' : 'var(--text-color, #fff)',
            opacity: activeTab === 'theme' ? 1 : 0.7,
          }}
        >
          Theme
        </button>
        <button
          onClick={() => setActiveTab('design')}
          className="flex-1 py-2.5 rounded-xl text-sm font-semibold transition-all"
          style={{
            backgroundColor: activeTab === 'design' ? 'var(--color-accent)' : 'rgba(255,255,255,0.06)',
            color: activeTab === 'design' ? '#fff' : 'var(--text-color, #fff)',
            opacity: activeTab === 'design' ? 1 : 0.7,
          }}
        >
          Design
        </button>
      </div>

      {activeTab === 'theme' && (
        <>
          <SectionHeader label="Presets" />
          <div className="grid grid-cols-3 gap-2 mb-4">
            {PRESET_OPTIONS.map(p => (
              <button
                key={p.key}
                onClick={() => applyPreset(p.key)}
                className="py-2.5 rounded-xl text-xs font-semibold transition-all"
                style={{
                  backgroundColor: activePreset === p.key ? 'var(--color-accent)' : 'rgba(255,255,255,0.06)',
                  color: activePreset === p.key ? '#fff' : 'var(--text-color, #fff)',
                  opacity: activePreset === p.key ? 1 : 0.6,
                  border: activePreset === p.key ? '2px solid var(--color-accent)' : '2px solid transparent',
                }}
              >
                {p.label}
              </button>
            ))}
          </div>

          <SectionHeader label="Colors" />
          <ColorRow label="Primary" value={primaryColor} onChange={setPrimaryColor} />
          <ColorRow label="Accent" value={accentColor} onChange={setAccentColor} />
          <ColorRow label="Text" value={textColor} onChange={setTextColor} />
          <ColorRow label="Card BG" value={cardBg} onChange={setCardBg} />
          <ColorRow label="Navbar BG" value={navbarBg} onChange={setNavbarBg} />

          <SectionHeader label="Transparency" />
          <OpacitySlider label="Navbar" value={navbarOpacity} onChange={setNavbarOpacity} />
          <OpacitySlider label="Cards" value={cardOpacity} onChange={setCardOpacity} />

          <SectionHeader label="Background" />
          <ToggleRow label="Gradient" value={gradientEnabled} onChange={setGradientEnabled} />
        </>
      )}

      {activeTab === 'design' && (
        <>
          <SectionHeader label="Card Gradient" />
          <ToggleRow label="Card Gradient" value={cardGradientEnabled} onChange={setCardGradientEnabled} />
          {cardGradientEnabled && (
            <>
              <OpacitySlider label="Gradient Angle" value={cardGradientAngle} onChange={setCardGradientAngle} min={0} max={360} suffix="°" />
              <ColorRow label="Gradient Color 1" value={cardGradientColor1} onChange={setCardGradientColor1} />
              <ColorRow label="Gradient Color 2" value={cardGradientColor2} onChange={setCardGradientColor2} />
            </>
          )}
        </>
      )}

      <button
        onClick={handleSave}
        disabled={saving}
        className="w-full py-4 rounded-xl font-semibold text-base flex items-center justify-center gap-2 mt-4"
        style={{ backgroundColor: 'var(--color-accent, #14b8a6)', color: '#fff', opacity: saving ? 0.6 : 1 }}
      >
        <Save size={18} />
        {saving ? 'Saving...' : 'Save & Apply'}
      </button>

      <div className="h-8" />
    </FullViewportOverlay>
  );
}
